import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useCare } from '../../contexts/CareContext'
import LoadingSpinner from '../shared/LoadingSpinner'
import EmptyState from '../shared/EmptyState'
import RequestCallModal from '../shared/RequestCallModal'
import { parseGoals } from '../../lib/carePlan'
import {
  GOAL_TYPES, STATUS, TRAJECTORY, goalTypeFor, buildGoalTypes,
  goalMetric, goalStatus, topDomainDeltas,
} from '../../lib/goalProgress'

const iso = (d) => d.toLocaleDateString('en-CA')
const fmtMonth = (m) =>
  m ? new Date(`${m}T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : null
const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null

/**
 * The four tests from the monthly functional assessment.
 * `betterIsUp` matters: a faster TUG time and a lower PHQ-2 are improvements,
 * so the arrow direction cannot be derived from the raw delta alone.
 */
const FUNCTIONAL_TESTS = [
  { key: 'mobility', emoji: '🚶', label: 'Mobility', unit: 's', betterIsUp: false,
    read: (a) => a.mobility_tug_seconds, hint: 'TUG seconds' },
  { key: 'memory', emoji: '🧠', label: 'Memory', unit: '/3', betterIsUp: true,
    read: (a) => a.memory_recall_score, hint: 'Recall score' },
  { key: 'strength', emoji: '💪', label: 'Strength', unit: '', betterIsUp: true,
    read: (a) => a.strength_chair_stands, hint: 'Chair stands' },
  { key: 'mood', emoji: '❤️', label: 'Mood', unit: '/6', betterIsUp: false,
    read: (a) => (a.phq2_q1 == null && a.phq2_q2 == null ? null : (a.phq2_q1 || 0) + (a.phq2_q2 || 0)),
    hint: 'PHQ-2' },
]

function FunctionalCard({ test, latest, prior }) {
  const now = latest ? test.read(latest) : null
  const was = prior ? test.read(prior) : null

  let arrow = '→'
  let color = 'var(--muted)'
  if (now != null && was != null && now !== was) {
    const improved = test.betterIsUp ? now > was : now < was
    arrow = now > was ? '↑' : '↓'
    color = improved ? 'var(--green)' : 'var(--amber)'
  }

  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="row" style={{ gap: 8 }}>
        <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>{test.emoji}</span>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{test.label}</span>
      </div>
      <div className="row" style={{ gap: 6, alignItems: 'baseline', marginTop: 6 }}>
        <span style={{ fontWeight: 800, fontSize: 20 }}>
          {now == null ? '—' : `${now}${test.unit}`}
        </span>
        <span style={{ color, fontWeight: 700, fontSize: 15 }} aria-hidden>{arrow}</span>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{test.hint}</div>
      {latest?.assessment_date && (
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Last assessed {fmtDate(latest.assessment_date)}
        </div>
      )}
    </div>
  )
}

export default function GoalProgressList() {
  const { recipient, canManageClinical } = useCare()

  const [loading, setLoading] = useState(true)
  const [plan, setPlan] = useState(null)
  const [logs, setLogs] = useState([])
  const [incidents, setIncidents] = useState([])
  const [progress, setProgress] = useState([])
  const [assessments, setAssessments] = useState([])
  const [callModal, setCallModal] = useState(null)

  const load = useCallback(async () => {
    if (!recipient) return
    const since = new Date(); since.setDate(since.getDate() - 27)
    const monthStart = new Date(); monthStart.setDate(1)

    const [planRes, logRes, incRes, progRes, assessRes] = await Promise.all([
      supabase.from('care_plans').select('*')
        .eq('care_recipient_id', recipient.id).eq('is_active', true)
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('care_logs')
        .select('log_date, mood_morning, mood_afternoon, mood_evening, fluids_ml, movement_json, task_activities')
        .eq('care_recipient_id', recipient.id).gte('log_date', iso(since)),
      supabase.from('incidents').select('id, incident_date')
        .eq('care_recipient_id', recipient.id).gte('incident_date', iso(monthStart)),
      supabase.from('monthly_progress').select('*')
        .eq('care_recipient_id', recipient.id).order('month', { ascending: false }).limit(2),
      supabase.from('functional_assessments').select('*')
        .eq('care_recipient_id', recipient.id).order('assessment_date', { ascending: false }).limit(2),
    ])

    setPlan(planRes.data || null)
    setLogs(logRes.data || [])
    setIncidents(incRes.data || [])
    setProgress(progRes.data || [])
    setAssessments(assessRes.data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  const goals = parseGoals(plan?.current_priorities)

  // Cache the detected types back onto the plan once, so a later human
  // correction has somewhere to live. Best-effort — never blocks the view.
  useEffect(() => {
    if (!plan || !goals.length || plan.goal_types || !canManageClinical) return
    supabase.from('care_plans')
      .update({ goal_types: buildGoalTypes(goals) })
      .eq('id', plan.id)
      // eslint-disable-next-line no-console
      .then(({ error }) => { if (error) console.warn('Could not cache goal types:', error.message) })
  }, [plan, goals, canManageClinical])

  if (!recipient || loading) return <LoadingSpinner label="Loading progress…" />

  const [latest, prior] = progress
  const traj = TRAJECTORY[latest?.trajectory] || null
  const deltas = topDomainDeltas(progress)

  return (
    <div className="stack">
      {/* ── Month over month ─────────────────────────────────── */}
      <div className="card stack" style={{ gap: 10 }}>
        <div className="spread">
          <div className="section-title">
            {latest ? `${fmtMonth(latest.month)} vs ${prior ? fmtMonth(prior.month) : '—'}` : 'This month'}
          </div>
          {traj && <span className={`badge ${traj.className}`}>{traj.label}</span>}
        </div>

        {!latest ? (
          <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.55 }}>
            No monthly assessment yet. A nurse review sets the baseline scores these
            trends are measured against.
          </p>
        ) : !prior ? (
          <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.55 }}>
            First month recorded — next month&rsquo;s review will show movement.
          </p>
        ) : deltas.length === 0 ? (
          <p className="muted" style={{ fontSize: 14, margin: 0 }}>No comparable domain scores yet.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {deltas.map((d) => {
              const up = d.delta > 0
              const flat = d.delta === 0
              const color = flat ? 'var(--muted)' : up ? 'var(--green)' : 'var(--amber)'
              return (
                <div key={d.key} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color }}>
                    {flat ? '–' : `${up ? '+' : ''}${d.delta}`}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>{d.label}</div>
                  <div className="muted" style={{ fontSize: 11 }}>{d.was} → {d.now}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Per-goal progress ────────────────────────────────── */}
      {goals.length === 0 ? (
        <EmptyState
          icon="📈"
          title="No goals to track yet"
          message="Once the care plan has goals, each one gets its own progress card here."
        />
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {goals.map((goal) => {
            const type = goalTypeFor(goal, plan?.goal_types)
            const metric = goalMetric({ type, goal, logs, incidents, progress })
            const status = STATUS[goalStatus(type, metric)]
            const t = GOAL_TYPES[type]
            return (
              <div key={goal.id} className="card stack" style={{ gap: 10 }}>
                <div className="spread" style={{ alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontWeight: 700, lineHeight: 1.4, minWidth: 0 }}>{goal.title}</div>
                  <span className={`badge ${t.className}`} style={{ flexShrink: 0 }}>{t.label}</span>
                </div>

                <div className="spread">
                  <div>
                    <div className="muted" style={{ fontSize: 12 }}>{metric.label}</div>
                    <div style={{ fontWeight: 800, fontSize: 18 }}>{metric.headline}</div>
                  </div>
                  <span className={`badge ${status.className}`}>{status.label}</span>
                </div>

                <MiniTrend weekly={metric.weekly} betterIsUp={metric.betterIsUp} label={metric.weeklyLabel} />
              </div>
            )
          })}
        </div>
      )}

      {/* ── Functional status ────────────────────────────────── */}
      <div>
        <div className="section-title" style={{ marginBottom: 8 }}>Functional status</div>
        {assessments.length === 0 ? (
          <div className="card muted" style={{ fontSize: 14, lineHeight: 1.55 }}>
            No assessments yet — run the monthly functional test in{' '}
            <strong>Tracking → Function</strong>.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {FUNCTIONAL_TESTS.map((t) => (
                <FunctionalCard
                  key={t.key} test={t}
                  latest={assessments[0]} prior={assessments[1]}
                />
              ))}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              Monthly assessments automatically update these scores.
              {plan?.next_review_date ? ` Next due: ${fmtDate(plan.next_review_date)}.` : ''}
            </div>
          </>
        )}
      </div>

      {/* ── Nurse review ─────────────────────────────────────── */}
      <div
        className="card stack"
        style={{ gap: 10, borderColor: 'var(--pa)', borderWidth: 1.5, background: 'var(--pa-light)' }}
      >
        <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 20, lineHeight: 1.2 }} aria-hidden>🩺</span>
          <div>
            <div style={{ fontWeight: 700, lineHeight: 1.4 }}>
              {plan?.next_review_date
                ? `Next review: ${fmtDate(plan.next_review_date)} with Mandy (LPN)`
                : 'Monthly nurse review with Mandy (LPN)'}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
              Monthly assessments automatically update your progress scores.
            </div>
          </div>
        </div>
        <button className="btn btn-sm" onClick={() => setCallModal('plus')}>
          Book nurse review →
        </button>
      </div>

      {callModal && <RequestCallModal plan={callModal} onClose={() => setCallModal(null)} />}
    </div>
  )
}

/** Four weekly bars, oldest to newest. A null week renders as an empty slot. */
function MiniTrend({ weekly, betterIsUp, label }) {
  const known = weekly.filter((v) => v != null)
  const max = known.length ? Math.max(...known, 1) : 1

  return (
    <div>
      <div className="row" style={{ gap: 6, alignItems: 'flex-end', height: 44 }}>
        {weekly.map((v, i) => {
          const pct = v == null ? 0 : Math.max(8, Math.round((v / max) * 100))
          const last = i === weekly.length - 1
          const tone = v == null
            ? 'var(--border)'
            : last
              ? (betterIsUp ? 'var(--green)' : 'var(--amber)')
              : 'var(--pa-light)'
          return (
            <div
              key={i}
              title={v == null ? 'No data' : `Week ${i + 1}: ${v}`}
              style={{
                flex: 1, height: `${v == null ? 8 : pct}%`, minHeight: 6,
                background: tone, borderRadius: 4,
                border: v == null ? '1px dashed var(--border)' : 'none',
              }}
            />
          )
        })}
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        4-week trend{label ? ` · ${label}` : ''} · oldest to newest
      </div>
    </div>
  )
}
